import { faClipboard } from '@fortawesome/free-regular-svg-icons/faClipboard';
import { faArrowUpFromBracket } from '@fortawesome/free-solid-svg-icons/faArrowUpFromBracket';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons/faArrowUpRightFromSquare';
import { faBell } from '@fortawesome/free-solid-svg-icons/faBell';
import { faCircleUser } from '@fortawesome/free-solid-svg-icons/faCircleUser';
import { faEnvelopeOpen } from '@fortawesome/free-solid-svg-icons/faEnvelopeOpen';
import { faGear } from '@fortawesome/free-solid-svg-icons/faGear';
import { faLink } from '@fortawesome/free-solid-svg-icons/faLink';
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle';
import { faQrcode } from '@fortawesome/free-solid-svg-icons/faQrcode';
import { faSquarePollVertical } from '@fortawesome/free-solid-svg-icons/faSquarePollVertical';
import { faSun } from '@fortawesome/free-solid-svg-icons/faSun';
import { faTableColumns } from '@fortawesome/free-solid-svg-icons/faTableColumns';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons/faUpRightFromSquare';
import { faUserCircle } from '@fortawesome/free-solid-svg-icons/faUserCircle';
import { faUsersGear } from '@fortawesome/free-solid-svg-icons/faUsersGear';
import { TFunction } from 'i18next';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavigateFunction, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import { Themes, useTheme } from '../ThemeContext';
import { Images } from '../assets';
import { generalTestIDs } from '../constants/test/general.testIds';
import useWindowDimensions from '../hooks/useWindowDimensions';
import { UserRoles } from '../model/user/types/UserRoles.enum';
import { adminSliceSelectors } from '../redux/admin/admin.selector';
import { atiraLinkSliceSelectors } from '../redux/atira-link/atira-link.selector';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { userSliceSelectors } from '../redux/user/user.selector';
import { userActions } from '../redux/user/user.slice';
import { NotificationsDrawer } from '../routes/shared/components/NotificationsDrawer';
import { SelectLinkToManageModal } from '../routes/shared/components/SelectLinkToManageModal';
import { ShareDrawer } from '../routes/shared/components/ShareDrawer';
import { SideBarRoutes } from '../routes/types/SideBarRoutes.enum';
import { Breakpoints } from '../theme/Breakpoints';
import { FontSize } from '../theme/FontSize';
import { Rounded } from '../theme/Rounded';
import { Spacing } from '../theme/Spacing';
import { AtiraToast } from '../utils/AtiraToast';
import { Badge, Menu, MenuItemType, MenuProps } from '../utils/local-antd';
import { AtiraDropdown } from './AtiraDropdown';
import { AtiraIcon } from './AtiraIcon';
import { Button } from './Button';
import { Flex } from './Flex';
import { AtiraImage } from './Image';
import { ImageCreateEdit } from './ImageCreateEdit';
import { LanguageSwitcher } from './LanguageSwitcher';
import { TextWithTruncated } from './TextWithTruncated';

const UserProfileWrapper = styled(Flex)`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 85%;
  aspect-ratio: 1 / 1;
  margin: auto;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const SideMenuThemedStyle = createGlobalStyle<{ collapsed: boolean }>`
  .ant-menu {
    background-color: ${({ theme }) => theme.main};
  }

  .ant-menu-title-content {
    display: flex;
  }

  .ant-menu-item:first-child {
    padding: ${Spacing.m} !important;
    overflow: visible;
  }

  .ant-menu-item:first-child:active {
    background-color: transparent !important;
  }

  .ant-menu-item  {
    padding: 0 !important;
    display: flex !important;
    align-items: center;
    gap: ${Spacing.m};
    margin: 0px;
    font-size: 1.6rem;
    height: 3.3rem;
    width: 100%;
    justify-content: center;
    padding-inline-start: ${({ collapsed }) => (collapsed ? '0' : Spacing.m)} !important;
  }

  .ant-menu-item:has(+ .user-profile) {
    margin-bottom: ${Spacing.xxl};
  }


  .ant-menu-item-selected path {
    fill: ${({ theme }) => theme.main};
  }

  .ant-menu-item-selected span {
    color: ${({ theme }) => theme.main} !important;
  }

  .toggle-theme:active, .translate:active, .user-profile:active {
    background-color: ${({ theme }) => theme.transparent} !important;
  }

  .ant-menu-item.translate {
    margin-top: 0 !important;
  }

  @media (max-width:${Breakpoints.DESKTOP}) {
    .ant-menu {
      display: flex;
      flex-direction: column;
    }
  }

  @media (max-height:${Breakpoints.MOBILE}) {
    .ant-menu {
      height: fit-content !important;
    }

    .ant-layout-sider-children {
      overflow-y: auto !important;

      scrollbar-width: none;
      -ms-overflow-style: none;
    }

    .ant-layout-sider-children::-webkit-scrollbar {
      display: none;
    }
  }
`;

const StyledMenu = styled(Menu)`
  position: relative;
  padding: 0;
  border-inline-end: 0 !important;

  .ant-menu-item.separator {
    height: fit-content;
    margin-bottom: ${Spacing.l};
    margin-top: calc(${Spacing.s} * 1.5);
    padding: 0 !important;
  }

  .separator .ant-menu-title-content {
    width: 100%;
  }

  @media (max-width: ${Breakpoints.DESKTOP}) {
    .ant-menu {
      height: 100%;
    }
    .ant-menu-item {
      padding: 0;
      display: flex;
    }

    .ant-menu-item.user-profile {
      overflow: visible;
    }

    .ant-menu-item:first-child {
      padding: 0 !important;
    }
  }
`;

const LangSwitcherWrapper = styled(Flex)`
  button {
    flex-direction: column;
    gap: 0.2rem;
  }
  margin: ${Spacing.s} ${Spacing.o};
`;

const MenuItemWrapper = styled(Flex)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${Spacing.s};
  width: 100%;
  text-align: left;

  p {
    font-size: inherit !important;
  }

  svg {
    width: 1.5rem;
  }
`;

const StyledButton = styled(Button)`
  background-color: ${({ theme }) => theme.transparent};
  margin: 0;
  padding: 0;
`;

const SettingsLink = styled(Link)`
  text-decoration: none;
  display: flex;
`;

const CollapsedLogoLink = styled(Link)`
  text-decoration: none;
  width: 75%;
`;

const ManageLinksButton = styled(StyledButton)`
  position: relative;

  div {
    gap: 0 !important;
  }
`;

const SelectedLinkImageWrapper = styled(Flex)`
  background-color: ${({ theme }) => theme.main};
  position: absolute;
  top: 0;
  left: 0;
  width: 1rem;
  aspect-ratio: 1 / 1;
  border-radius: ${Rounded.circle};
  overflow: hidden;

  path {
    fill: ${({ theme }) => theme.onMain} !important;
  }

  .ant-image {
    width: 100% !important;
    height: 100% !important;
  }

  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    display: block;
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;

const StyledBadge = styled(Badge)`
  display: flex;

  .ant-badge-count {
    background-color: ${({ theme }) => theme.onMain} !important;
  }

  .ant-scroll-number-only-unit {
    color: ${({ theme }) => theme.main} !important;
  }
`;

const UserImageWrapper = styled(Flex)`
  img {
    border-radius: ${Rounded.circle};
  }
`;

const IconGroupWrapper = styled(Flex)`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.onMain};
`;

const SideMenuSpan = styled.span`
  font-size: calc(${FontSize.m} + 0.3rem);
`;

const Separator = styled.hr`
  border: 0;
  height: 2px;
  width: 100%;
  margin: 0;
  opacity: 1;
  background-color: ${({ theme }) => theme.onMain};
`;

export const SideMenu: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const [selectLinkModalVisible, setSelectLinkModalVisible] = useState(false);
  const [notificationsDrawerVisible, setNotificationsDrawerVisible] =
    useState(false);
  const [shareDrawerVisible, setShareDrawerVisible] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { width } = useWindowDimensions();
  const { theme, toggleTheme, currentTheme } = useTheme();

  const dispatch = useAppDispatch();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const selectedLink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );
  const unreadMessages = useAppSelector(
    adminSliceSelectors.selectAdminMessages,
  )?.filter(({ read }) => read === false);

  const onLogout = () => {
    dispatch(userActions.logout());
    navigate('/login', { replace: true });
  };

  const copyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    AtiraToast.success(t('common.copySuccess'));
  };

  const getMenuItems = (config: {
    t: TFunction;
    navigate: NavigateFunction;
    collapsed: boolean;
  }): Required<MenuProps>['items'] => {
    const { t, navigate, collapsed } = config;

    const userItems: MenuProps['items'] = [
      {
        key: 'g1',
        type: 'group',
        children: [
          {
            key: 'email',
            title: '',
            label: user?.email ? (
              <MenuItemWrapper>
                <AtiraIcon icon={faEnvelopeOpen} />
                <TextWithTruncated length={20} textProps={{ direction: 'ltr' }}>
                  {user.email}
                </TextWithTruncated>

                <Flex marginInlineStart="auto">
                  <AtiraIcon icon={faClipboard} color="iconPrimary" />
                </Flex>
              </MenuItemWrapper>
            ) : (
              <MenuItemWrapper>
                <AtiraIcon icon={faCircleUser} />
                {t('common.user_email')}
              </MenuItemWrapper>
            ),
            onClick: () => {
              if (user?.email) {
                copyEmail(user.email);
              }
            },
          },
          {
            key: 'share',
            title: '',
            label: (
              <MenuItemWrapper>
                <AtiraIcon icon={faArrowUpFromBracket} />
                {t('common.share')}
              </MenuItemWrapper>
            ),
            onClick: () => setShareDrawerVisible(true),
          },
          ...(user?.roles.includes(UserRoles.ADMIN)
            ? [
                {
                  key: 'admin',
                  title: '',
                  label: (
                    <MenuItemWrapper>
                      <AtiraIcon icon={faUsersGear} />
                      {t('admin.header.title')}
                    </MenuItemWrapper>
                  ),
                  onClick: () => navigate('/admin'),
                },
              ]
            : []),

          {
            key: '4',
            title: '',
            label: (
              <MenuItemWrapper>
                <AtiraIcon icon={faGear} />
                {t('header.subscription')}
              </MenuItemWrapper>
            ),
            onClick: () => navigate('/subscription'),
          },
        ],
      },
      {
        key: '4',
        type: 'divider',
      },
      {
        key: '5',
        title: '',
        className: 'logout-button',
        label: (
          <MenuItemWrapper>
            <Button
              onClick={onLogout}
              backgroundColor="danger"
              color="onDanger"
              textAlign="center"
              width="100%"
            >
              {t('header.logout')}
            </Button>
          </MenuItemWrapper>
        ),
      },
    ];

    const items: MenuItemType[] = [
      {
        key: 'atira-link',
        label: !collapsed ? (
          <IconGroupWrapper>
            <Flex>
              <Link to={'/dashboard'}>
                <AtiraImage src={Images.AtiraLinkSubLogo} />
              </Link>
            </Flex>
          </IconGroupWrapper>
        ) : (
          <IconGroupWrapper>
            <CollapsedLogoLink to={'/dashboard'}>
              <AtiraImage src={Images.AtiraLinkSubIcon} width={25} />
            </CollapsedLogoLink>
          </IconGroupWrapper>
        ),
        title: '',
      },
      {
        key: 'separator',
        label: <Separator />,
        title: '',
        className: 'separator',
      },
      {
        key: SideBarRoutes.DASHBOARD,
        label: (
          <IconGroupWrapper dataTestId={generalTestIDs.sideMenu.dashboard}>
            {!collapsed ? (
              <MenuItemWrapper>
                <Flex width={'25%'}>
                  <AtiraIcon icon={faTableColumns} size="sm" />
                </Flex>
                <SideMenuSpan>{t('side_menu.dashboard')}</SideMenuSpan>
              </MenuItemWrapper>
            ) : (
              <AtiraIcon icon={faTableColumns} />
            )}
          </IconGroupWrapper>
        ),
        onClick: () => navigate(SideBarRoutes.DASHBOARD),
        title: collapsed && width > 992 ? t('side_menu.dashboard') : '',
      },
      {
        key: SideBarRoutes.MY_LINK,
        label: (
          <IconGroupWrapper dataTestId={generalTestIDs.sideMenu.myLink}>
            {!collapsed ? (
              <MenuItemWrapper>
                <Flex width={'25%'}>
                  <AtiraIcon icon={faArrowUpRightFromSquare} size="sm" />
                </Flex>
                <SideMenuSpan>{t('side_menu.my_link')}</SideMenuSpan>
              </MenuItemWrapper>
            ) : (
              <AtiraIcon icon={faUpRightFromSquare} />
            )}
          </IconGroupWrapper>
        ),
        onClick: () => navigate(SideBarRoutes.MY_LINK),
        title: collapsed && width > 992 ? t('side_menu.my_link') : '',
      },
      {
        key: SideBarRoutes.MY_URLS,
        label: (
          <IconGroupWrapper dataTestId={generalTestIDs.sideMenu.myURLs}>
            {!collapsed ? (
              <MenuItemWrapper>
                <Flex width={'25%'}>
                  <AtiraIcon icon={faLink} size="sm" />
                </Flex>
                <SideMenuSpan>{t('side_menu.my_urls')}</SideMenuSpan>
              </MenuItemWrapper>
            ) : (
              <AtiraIcon icon={faLink} />
            )}
          </IconGroupWrapper>
        ),
        onClick: () => navigate(SideBarRoutes.MY_URLS),
        title: collapsed && width > 992 ? t('side_menu.my_urls') : '',
      },
      {
        key: SideBarRoutes.MY_QRS,
        label: (
          <IconGroupWrapper dataTestId={generalTestIDs.sideMenu.myQRs}>
            {!collapsed ? (
              <MenuItemWrapper>
                <Flex width={'25%'}>
                  <AtiraIcon icon={faQrcode} size="sm" />
                </Flex>
                <SideMenuSpan>{t('side_menu.my_qrs')}</SideMenuSpan>
              </MenuItemWrapper>
            ) : (
              <AtiraIcon icon={faQrcode} />
            )}
          </IconGroupWrapper>
        ),
        onClick: () => navigate(SideBarRoutes.MY_QRS),
        title: collapsed && width > 992 ? t('side_menu.my_qrs') : '',
      },
      {
        key: SideBarRoutes.ANALYTICS,
        label: (
          <IconGroupWrapper dataTestId={generalTestIDs.sideMenu.analytics}>
            {!collapsed ? (
              <MenuItemWrapper>
                <Flex width={'25%'}>
                  <AtiraIcon icon={faSquarePollVertical} size="sm" />
                </Flex>
                <SideMenuSpan>{t('side_menu.analytics')}</SideMenuSpan>
              </MenuItemWrapper>
            ) : (
              <AtiraIcon icon={faSquarePollVertical} />
            )}
          </IconGroupWrapper>
        ),
        onClick: () => navigate(SideBarRoutes.ANALYTICS),
        title: collapsed && width > 992 ? t('side_menu.analytics') : '',
      },
    ];

    if (width <= Number(Breakpoints.DESKTOP.replace('px', ''))) {
      items.push(
        {
          title: '',
          key: 'profile',
          label: (
            <IconGroupWrapper>
              <AtiraDropdown
                overlayStyle={{ width: '15rem', borderRadius: Rounded.lg }}
                items={userItems}
                placement="topRight"
                arrow={{ pointAtCenter: true }}
                trigger={['click']}
              >
                <UserProfileWrapper>
                  {user?.picture ? (
                    <UserImageWrapper>
                      <AtiraImage src={user.picture} />
                    </UserImageWrapper>
                  ) : (
                    <AtiraIcon icon={faCircleUser} size="2x" color="onMain" />
                  )}
                </UserProfileWrapper>
              </AtiraDropdown>
            </IconGroupWrapper>
          ),
          className: 'user-profile',
          style: { marginTop: 'auto' },
        },
        {
          key: 'notifications',
          title: '',
          label: (
            <IconGroupWrapper>
              <StyledBadge
                count={unreadMessages?.length}
                color={unreadMessages?.length ? theme.main : theme.transparent}
              >
                <StyledButton
                  icon={faBell}
                  iconWidth="2x"
                  iconColor="onMain"
                  onClick={() => setNotificationsDrawerVisible(true)}
                />
              </StyledBadge>
            </IconGroupWrapper>
          ),
          className: 'sidemenu-notifications',
          style: { marginTop: Spacing.s },
        },
        {
          key: 'links-managment',
          title: '',
          label: (
            <IconGroupWrapper>
              <ManageLinksButton
                onClick={() => setSelectLinkModalVisible(true)}
                icon={faLink}
                iconWidth="2x"
                iconColor="onMain"
              >
                <SelectedLinkImageWrapper>
                  {selectedLink?.image ? (
                    <ImageCreateEdit src={selectedLink.image} />
                  ) : (
                    <AtiraIcon
                      icon={selectedLink ? faUserCircle : faPlusCircle}
                      color="onMain"
                    />
                  )}
                </SelectedLinkImageWrapper>
              </ManageLinksButton>
            </IconGroupWrapper>
          ),
          style: { marginTop: Spacing.s },
        },
        {
          key: 'translate',
          title: '',
          label: (
            <LangSwitcherWrapper>
              <LanguageSwitcher popupPlacement="topRight" color="onMain" />
            </LangSwitcherWrapper>
          ),
          style: { height: 'auto', marginTop: Spacing.m },
          className: 'translate',
        },
        {
          key: 'toggle-theme',
          title: '',
          label: (
            <IconGroupWrapper>
              <StyledButton
                icon={currentTheme === Themes.LIGHT ? faMoon : faSun}
                onClick={toggleTheme}
                color="onMain"
                iconWidth="2x"
              />
            </IconGroupWrapper>
          ),
          className: 'toggle-theme',
        },
        {
          key: 'settings',
          title: '',
          label: (
            <IconGroupWrapper>
              <SettingsLink to={'/settings'}>
                <AtiraIcon icon={faGear} />
              </SettingsLink>
            </IconGroupWrapper>
          ),
        },
      );
    }

    return items;
  };

  const getSelectedKey = useMemo(() => {
    switch (true) {
      case pathname.startsWith(SideBarRoutes.MY_LINK):
        return SideBarRoutes.MY_LINK;
      default:
        return pathname;
    }
  }, [pathname]);

  return (
    <Fragment>
      <StyledMenu
        mode="inline"
        theme="dark"
        items={getMenuItems({ t, navigate, collapsed })}
        selectedKeys={[getSelectedKey]}
      />

      <SelectLinkToManageModal
        onClose={() => setSelectLinkModalVisible(false)}
        open={selectLinkModalVisible}
      />
      <NotificationsDrawer
        open={notificationsDrawerVisible}
        onClose={() => setNotificationsDrawerVisible(false)}
      />
      <ShareDrawer
        open={shareDrawerVisible}
        onClose={() => setShareDrawerVisible(false)}
      />
      <SideMenuThemedStyle theme={theme} collapsed={collapsed} />
    </Fragment>
  );
};
